import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import {
  BoltaAmendItemSchema,
  BoltaBusinessRegistrationNumberSchema,
  BoltaDateStringSchema,
  BoltaInvoiceItemSchema,
  BoltaPurposeSchema,
  BoltaRecipientPartySchema,
  BoltaReverseRecipientPartySchema,
  BoltaSupplierPartySchema,
  BoltaTaxRegistrationIdSchema,
  BoltaTaxTypeSchema,
} from "@app/lib/api/actions/servers/bolta/types";
import { z } from "zod";

const SUPPLIER_KEY_SCHEMA = z
  .string()
  .min(1)
  .optional()
  .describe(
    "The supplierKey identifying the issuing company (공급자). OMIT this in " +
      "the normal case — it is resolved automatically from the workspace's " +
      "registered supplier. Only pass it when the workspace has multiple " +
      "registered suppliers. It must belong to this workspace."
  );

const ISSUANCE_KEY_SCHEMA = z
  .string()
  .min(1)
  .describe("The issuanceKey returned when the invoice was issued.");

const CLIENT_REFERENCE_ID_SCHEMA = z
  .string()
  .min(1)
  .max(100)
  .describe(
    "Unique idempotency key for this issuance (e.g. an order or contract " +
      "id plus a month, like 'INV-ACME-2026-07'). Re-sending the same id " +
      "returns the existing result instead of issuing a duplicate invoice. " +
      "Also usable later with get_issuance_status."
  );

export const BOLTA_TOOLS_METADATA = [
  {
    name: "register_supplier",
    description:
      "Register the issuing company (공급자) with the tax-invoice issuance " +
      "system and obtain its " +
      "supplierKey. Must be done once per issuing company before any " +
      "issuance. Save the returned supplierKey — every other tool needs it. " +
      "After registration, the company's certificate (공동인증서) must be " +
      "registered via the URL from get_certificate_registration_url before " +
      "invoices can be issued.",
    schema: {
      identificationNumber: BoltaBusinessRegistrationNumberSchema.describe(
        "사업자등록번호 of the issuing company (10 digits, no hyphens)."
      ),
      taxRegistrationId: BoltaTaxRegistrationIdSchema.nullish().describe(
        "종사업장번호 (4 digits), only if the company has one."
      ),
      organizationName: z
        .string()
        .min(1)
        .max(70)
        .describe("Registered company name (상호명)."),
      representativeName: z
        .string()
        .min(1)
        .max(30)
        .describe("Representative name (대표자명)."),
      managerEmail: z
        .string()
        .email()
        .max(40)
        .nullish()
        .describe("Contact email for the issuing company (optional)."),
    },
    stake: "low",
    displayLabels: {
      running: "Registering supplier",
      done: "Register supplier",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_supplier",
    description:
      "Get a registered supplier's info and certificate (공동인증서) status. " +
      "Use this before issuing to check the certificate is registered and " +
      "not expired — issuance fails without a valid certificate.",
    schema: {
      supplierKey: SUPPLIER_KEY_SCHEMA,
    },
    stake: "never_ask",
    displayLabels: {
      running: "Checking supplier",
      done: "Check supplier",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_certificate_registration_url",
    description:
      "Get the URL where the supplier registers their 공동인증서 " +
      "(certificate) on a secure hosted page. The certificate never touches " +
      "this platform. Share the URL with the user; after they complete " +
      "registration, verify with get_supplier.",
    schema: {
      supplierKey: SUPPLIER_KEY_SCHEMA,
    },
    stake: "never_ask",
    displayLabels: {
      running: "Getting certificate registration URL",
      done: "Get certificate registration URL",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "issue_tax_invoice",
    description:
      "Issue a Korean electronic tax invoice (전자세금계산서 정발행) through " +
      "the NTS-certified issuance system. THIS IS A LEGALLY BINDING ACT: " +
      "the invoice is signed with the " +
      "supplier's certificate and transmitted to the National Tax Service " +
      "(국세청). Before calling, always show the user the complete invoice " +
      "content (supplier, recipient, items, amounts, tax) and get their " +
      "explicit confirmation. Total invoice amount = sum of item amounts. " +
      "For TAXABLE, each item's tax is normally 10% of its supplyCost. " +
      "Mistakes can only be corrected via amendment invoices (수정발행).",
    schema: {
      supplierKey: SUPPLIER_KEY_SCHEMA,
      clientReferenceId: CLIENT_REFERENCE_ID_SCHEMA,
      date: BoltaDateStringSchema.describe(
        "Invoice date (작성일자, YYYY-MM-DD). Legally must be within the " +
          "supply period; late issuance past the 10th of the following " +
          "month incurs penalties (가산세)."
      ),
      purpose: BoltaPurposeSchema.describe(
        "RECEIPT (영수): payment already received. CLAIM (청구): requesting " +
          "payment."
      ),
      taxType: BoltaTaxTypeSchema.describe(
        "TAXABLE (과세, 전자세금계산서), ZERO_RATE (영세율), or TAX_FREE " +
          "(면세, 전자계산서)."
      ),
      supplier: BoltaSupplierPartySchema.describe(
        "The issuing company (공급자). Must match the registered supplier."
      ),
      supplied: BoltaRecipientPartySchema.describe(
        "The recipient (공급받는자) receiving the invoice."
      ),
      items: z
        .array(BoltaInvoiceItemSchema)
        .min(1)
        .max(16)
        .describe("Invoice line items (품목), 1 to 16."),
      description: z
        .string()
        .min(1)
        .max(150)
        .nullish()
        .describe(
          "Invoice remark (비고). Must be omitted when the recipient is " +
            "identified by a foreign registration number."
        ),
    },
    stake: "high",
    displayLabels: {
      running: "Issuing tax invoice",
      done: "Issue tax invoice",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "amend_contract_termination",
    description:
      "Issue an amendment invoice (수정세금계산서) for contract termination " +
      "(계약의 해제). Offsets the original invoice entirely. LEGALLY " +
      "BINDING — confirm with the user first. Must be issued by the 10th " +
      "of the month following the termination to avoid penalties. Not " +
      "allowed when the original invoice has negative item amounts.",
    schema: {
      supplierKey: SUPPLIER_KEY_SCHEMA,
      clientReferenceId: CLIENT_REFERENCE_ID_SCHEMA,
      issuanceKey: ISSUANCE_KEY_SCHEMA.describe(
        "issuanceKey of the ORIGINAL invoice being amended."
      ),
      date: BoltaDateStringSchema.describe(
        "Contract termination date (계약 해제일, YYYY-MM-DD)."
      ),
    },
    stake: "high",
    displayLabels: {
      running: "Issuing contract-termination amendment",
      done: "Issue contract-termination amendment",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "amend_supply_cost_change",
    description:
      "Issue an amendment invoice (수정세금계산서) for a supply value change " +
      "(공급가액 변동), e.g. a price adjustment or partial refund. Items " +
      "express the DELTA versus the original (positive to add, negative to " +
      "subtract). LEGALLY BINDING — confirm with the user first. Must be " +
      "issued by the 10th of the month following the change event.",
    schema: {
      supplierKey: SUPPLIER_KEY_SCHEMA,
      clientReferenceId: CLIENT_REFERENCE_ID_SCHEMA,
      issuanceKey: ISSUANCE_KEY_SCHEMA.describe(
        "issuanceKey of the ORIGINAL invoice being amended."
      ),
      date: BoltaDateStringSchema.describe(
        "Date the supply value changed (공급가액 변동일, YYYY-MM-DD)."
      ),
      taxType: BoltaTaxTypeSchema,
      items: z
        .array(BoltaAmendItemSchema)
        .min(1)
        .max(16)
        .describe("Delta line items (변동분 품목), 1 to 16."),
    },
    stake: "high",
    displayLabels: {
      running: "Issuing supply-cost-change amendment",
      done: "Issue supply-cost-change amendment",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "amend_double_issuance",
    description:
      "Issue an amendment invoice (수정세금계산서) cancelling an invoice " +
      "issued twice by mistake (착오에 의한 이중발급). Offsets the original " +
      "invoice; the original invoice date is applied automatically. LEGALLY " +
      "BINDING — confirm with the user first.",
    schema: {
      supplierKey: SUPPLIER_KEY_SCHEMA,
      clientReferenceId: CLIENT_REFERENCE_ID_SCHEMA,
      issuanceKey: ISSUANCE_KEY_SCHEMA.describe(
        "issuanceKey of the duplicate invoice to offset."
      ),
    },
    stake: "high",
    displayLabels: {
      running: "Issuing double-issuance amendment",
      done: "Issue double-issuance amendment",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "request_reverse_issuance",
    description:
      "Request reverse issuance (역발행): the recipient (공급받는자) drafts " +
      "the invoice and asks the supplier to approve and issue it. Sends an " +
      "approval request to the supplier's contacts. Confirm the full " +
      "content with the user before calling. The supplier approves via " +
      "email or via the URL from get_reverse_issuance_approval_url.",
    schema: {
      supplierKey: SUPPLIER_KEY_SCHEMA.describe(
        "supplierKey of the SUPPLIER who will approve and issue (registered " +
          "via register_supplier)."
      ),
      clientReferenceId: CLIENT_REFERENCE_ID_SCHEMA,
      date: BoltaDateStringSchema.describe("Invoice date (작성일자)."),
      purpose: BoltaPurposeSchema.describe("RECEIPT (영수) or CLAIM (청구)."),
      taxType: BoltaTaxTypeSchema,
      supplier: BoltaSupplierPartySchema.describe(
        "The supplier (공급자) who is asked to issue."
      ),
      supplied: BoltaReverseRecipientPartySchema.describe(
        "The recipient (공급받는자) requesting the issuance — a business " +
          "with a 사업자등록번호."
      ),
      items: z
        .array(BoltaInvoiceItemSchema)
        .min(1)
        .max(16)
        .describe("Invoice line items (품목), 1 to 16."),
      description: z
        .string()
        .min(1)
        .max(150)
        .nullish()
        .describe("Invoice remark (비고), optional."),
    },
    stake: "high",
    displayLabels: {
      running: "Requesting reverse issuance",
      done: "Request reverse issuance",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_reverse_issuance_approval_url",
    description:
      "Get the approval URL for a pending reverse issuance (역발행) request. " +
      "The URL is valid for 10 minutes and requires the supplier's " +
      "certificate to be registered. Share it with the supplier to approve.",
    schema: {
      supplierKey: SUPPLIER_KEY_SCHEMA,
      issuanceKey: ISSUANCE_KEY_SCHEMA.describe(
        "issuanceKey returned by request_reverse_issuance."
      ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Getting reverse-issuance approval URL",
      done: "Get reverse-issuance approval URL",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "cancel_reverse_issuance",
    description:
      "Cancel a pending reverse issuance (역발행) request. Fails if the " +
      "request was already approved, cancelled, or issued.",
    schema: {
      supplierKey: SUPPLIER_KEY_SCHEMA,
      issuanceKey: ISSUANCE_KEY_SCHEMA.describe(
        "issuanceKey returned by request_reverse_issuance."
      ),
    },
    stake: "low",
    displayLabels: {
      running: "Cancelling reverse-issuance request",
      done: "Cancel reverse-issuance request",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_tax_invoice",
    description:
      "Get the content of a COMPLETED tax invoice, including the NTS " +
      "approval number (국세청승인번호). Returns nothing until issuance is " +
      "complete — for in-progress requests use get_issuance_status.",
    schema: {
      issuanceKey: ISSUANCE_KEY_SCHEMA,
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving tax invoice",
      done: "Retrieve tax invoice",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_issuance_status",
    description:
      "Check the processing status of an issuance request by its " +
      "clientReferenceId. Use this to verify whether an issuance went " +
      "through (e.g. after an ambiguous failure) before ever retrying.",
    schema: {
      clientReferenceId: z
        .string()
        .min(1)
        .describe("The clientReferenceId used when issuing."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Checking issuance status",
      done: "Check issuance status",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
] as const;

export const BOLTA_SERVER = {
  serverInfo: {
    name: "tax_invoice",
    version: "1.0.0",
    description:
      "Issue and manage Korean electronic tax invoices (전자세금계산서) " +
      "through a National Tax Service certified issuance system: standard " +
      "issuance (정발행), amendments (수정발행), reverse " +
      "issuance (역발행), and supplier/certificate management.",
    authorization: null,
    icon: "ActionDocumentTextIcon",
    documentationUrl: null,
  },
  tools: BOLTA_TOOLS_METADATA,
} as const satisfies ServerMetadata;
